import flatten from 'lodash-es/flatten';
import max from 'lodash-es/max';
import min from 'lodash-es/min';
import { Model } from '@df/catalog/model';
import { ListingFilter } from '@df/catalog-ui/listing/listing';
import { FilterAttribute } from '@df/catalog-ui/listing/filter/filter-attribute';

export interface ISelection {
    // min selection
    min?: number;
    // max selection
    max?: number;
    // sale only
    // sale?: number,
}

export interface IRangeMeta {
    min: number;
    max: number;
}

export type IExtractor<T extends Model> = (model: T) => number | undefined;

@ListingFilter({
    filterType: 'attribute-range'
})
export class FilterAttributeRange<T extends Model> extends FilterAttribute<T, ISelection> {
    /**
     * Range filter alway has matches
     */
    hasAnyMatches = true;

    /**
     * Initial selection
     */
    protected override _selection: ISelection = {};

    private _multiSelection: IRangeMeta[] | undefined;
    /**
     * Cached metadata
     */
    private _metaData?: IRangeMeta;

    /**
     * Cached metadata for currently displayed models
     */
    private _currentMetaData?: IRangeMeta;
    // private anyCurrentMatchesSale: any | undefined;

    protected override onInit() {
        // if models change reset all meta data (we dont need to update this data, it will be auto set when needed)
        this.listing.subscribe(() => {
            this._metaData = undefined;
            this._currentMetaData = undefined;
        });
    }

    /**
     * Max value getter
     */
    get max(): number | undefined {
        return this.selection?.max;
    }

    /**
     * Max value setter
     */
    set max(value: number | undefined) {
        if (!this.selection || this.selection.max !== value) {
            this.setMax(value);
            // this.selection.sale = undefined;
            this.next();
        }
    }

    setMax(value: number | undefined) {
        if (!this.selection) {
            this.selection = {};
        }
        this.selection.max = this.cleanup(value);
        this._multiSelection = undefined;
    }

    /**
     * Min value getter
     */
    get min(): number | undefined {
        return this.selection?.min;
    }

    /**
     * Min value setter
     */
    set min(value: number | undefined) {
        if (!this.selection || this.selection.min !== value) {
            this.setMin(value);
            // this.selection.sale = undefined;
            this.next();
        }
    }

    setMin(value: number | undefined) {
        if (!this.selection) {
            this.selection = {};
        }
        this.selection.min = this.cleanup(value);
        this._multiSelection = undefined;
    }

    /**
     * Multiselection hook
     *
     * If you want to apply more ranges at the same time
     */
    setMultiSelection(ranges: IRangeMeta[] | undefined) {
        this._multiSelection = ranges?.length ? ranges : undefined;
        this._selection = {};
        this.next();
    }

    getMultiSelection(): IRangeMeta[] | undefined {
        return this._multiSelection;
    }
    /**
     * Sale value getter
     */
    // set sale(value: number | undefined) {
    //     this.selection.sale = this.cleanup(value);
    //     this.next();
    // }

    /**
     * Sale value setter
     */
    // get sale(): number | undefined {
    //     return this.selection.sale;
    // }

    /**
     * Checks if any model is sale
     * Current models are checked
     * @return {boolean} true if any matches
     */
    // public get hasAnyCurrentMatchesSale(): boolean {
    //     if (this.anyCurrentMatchesSale == undefined) {
    //         const models: T[] = this.listing.getFilteredModelsExceptFilter(
    //             true
    //         );
    //         const matches = _.find(models, model => {
    //             return model.data && model.data['org_price'];
    //         });

    //         this.anyCurrentMatchesSale = !!matches;
    //     }

    //     return this.anyCurrentMatchesSale;
    // }

    /**
     * Meta data getter
     */
    getMetaData(): IRangeMeta {
        if (this._metaData === undefined) {
            this._metaData = this.calcMetaData(this.listing.getSourceModels());
        }
        return this._metaData;
    }

    getCurrentMetaData(): IRangeMeta {
        if (this._currentMetaData === undefined) {
            this._currentMetaData = this.calcMetaData(this.listing.getFilteredModelsExceptFilter([{ filter: this, selection: false }]));
        }
        return this._currentMetaData;
    }

    private calcMetaData(models: T[]): IRangeMeta {
        const extrector = this.getExtractor();
        const rangeValues = flatten(models.map(extrector).filter(i => !!i));
        return {
            min: (rangeValues.length && min(rangeValues)) || 0,
            max: (rangeValues.length && max(rangeValues)) || 0
        };
    }
    /**
     * Generates xtractor function that gets  preperty from model
     */
    protected getExtractor(): IExtractor<T> {
        const attributeCode = this.attribute.id;
        if (attributeCode === 'price') {
            return model => model['price'];
        }
        return model => (model.data ? model.data[attributeCode] : undefined);
    }

    /**
     * Helper method cleaning up values before setting
     */
    private cleanup(value: number | string | undefined): number | undefined {
        let val: number | undefined;

        if (value === 0) {
            val = 0;
        } else if (!value) {
            val = undefined;
        } else if (typeof value === 'number') {
            val = value;
        } else {
            val = parseFloat(value);
        }

        if (typeof val === 'number' && val < 0) {
            val = 0;
        }

        return val;
    }

    /**
     * Resets selection
     */
    public reset() {
        if (this.hasSelection) {
            this._multiSelection = undefined;
            if (!this.selection) {
                this.selection = {};
            }
            this.selection.min = undefined;
            this.selection.max = undefined;

            // this.selection.sale = undefined;
            return this.next();
        }
    }

    /**
     * Applies filter to models collection
     * @param  {T[]} collection Input collection to filter
     * @return {T[]}            Filtered collection
     */
    public applyFilter(collection: T[]): T[] {
        // do not apply if no bounds set
        if (!this.hasSelection) {
            return collection;
        }
        // filtered attribute code
        const extrector = this.getExtractor();
        // filter collection
        return collection.filter(model => {
            const value = extrector(model);

            // if value is not set then false
            if (value === undefined) {
                return false;
            }

            if (this._multiSelection) {
                return this._multiSelection.find(range => checkRange(range.min, range.max));
            } else {
                return checkRange(this.min, this.max);
            }

            function checkRange(checkedMin: number | undefined, checkedMax: number | undefined) {
                // check min value if set
                if (checkedMin !== undefined && checkedMin > value!) {
                    return false;
                }

                // check max value if set
                if (checkedMax !== undefined && checkedMax < value!) {
                    return false;
                }

                // show only sale products
                // if (this.sale !== undefined) {
                //     return !!(model.data && model.data['org_price']);
                // }

                return true;
            }
        });
    }

    /**
     * Serializes selection to string
     */
    getState(): string {
        if (!this.hasSelection) {
            return '';
        }

        if (this._multiSelection) {
            return (
                this._multiSelection
                    .map(i => [typeof i.min === 'number' ? i.min : '', typeof i.max === 'number' ? i.max : ''].join())
                    .join(';') + ';'
            );
        }

        return [
            this.min || '',
            this.max || ''
            // , (this._sale || '')
        ].join();
    }

    /**
     * Unserializes string | array to selection
     */
    setState(value: string | number[]) {
        if (value) {
            if (Array.isArray(value)) {
                // hacky solution to deal with semicolons
                if (value.find(i => typeof i === 'string' && (<string>i).includes(';'))) {
                    return this.setState(value.join());
                }
                this.setMin(value[0]);
                this.setMax(value[1]);
                // this.sale = value[2];
            } else {
                if (value.includes(';')) {
                    this.setMultiSelection(
                        value
                            .split(';')
                            .map(i => {
                                const parts = i.split(',', 2).map(j => this.cleanup(j));
                                if (parts?.length === 2) {
                                    return { min: parts[0], max: parts[1] };
                                }
                                return undefined;
                            })
                            .filter(i => i && typeof i.min === 'number') as IRangeMeta[]
                    );
                } else {
                    this.setState(
                        value
                            .split(',', 2)
                            .map(i => this.cleanup(i))
                            .filter(i => !!i) as number[]
                    );
                }
            }
        }
    }

    /**
     * Checks if filter is applied
     * @return {boolean}
     */
    get hasSelection(): boolean {
        return !!this._multiSelection || (!!this.selection && !(this.selection.min === undefined && this.selection.max === undefined));
        // && this.selection.sale === undefined
    }
}
